import { ExtendedBooking } from '@wix/ambassador-bookings-reader-v2-extended-booking/types';
import { Member } from '@wix/ambassador-members-ng-api/types';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import React, { useEffect, useMemo } from 'react';
import { ExperimentsConsts } from '../../../../consts/experiments';
import { CreateBookingErrorType, FormError } from '../../../../types/errors';
import { FormStatus } from '../../../../types/form-state';
import { CartFlow } from '../../../../types/types';
import { mapToArray } from '../../../../utils';
import { getErrorByType } from '../../../../utils/errors/errors';
import {
  checkDifferentBuyPlans,
  getExceedLimitedPricePlans,
  getSelectedServicesForBuyPlan,
  hasPurchasablePlanSelected,
  hasServicesWithoutAvailablePlans,
  wouldShowBuyPlanAndBook,
} from '../../../../utils/payment';
import {
  ServiceData,
  SlotService,
  SlotServices,
} from '../../../../utils/state/types';
import { formatBookingTime } from '../../../../utils/time';
import { useFormActions } from '../../Hooks/useFormActions';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import {
  SectionMessage,
  SectionMessageType,
} from '../SectionMessage/SectionMessage';
import { AddCartButton } from './AddCartButton';
import { BookButton } from './BookButton';
import { classes } from './ButtonContainer.st.css';
import { BuyPlanButton } from './BuyPlanButton';
import { ButtonsContainerDataHooks } from './dataHooks';

export interface ButtonContainerProps {
  serviceData: ServiceData;
  status: FormStatus;
  selectedNumberOfParticipants?: number;
  errors: FormError[];
  isCart?: boolean;
  isServiceInCart?: boolean;
  existingBookingDetails?: ExtendedBooking;
  memberDetails?: Member;
  isSlotUnavailable?: boolean;
}

interface NotificationMessageProps {
  slotServices: SlotServices;
  existingBookingDetails?: ExtendedBooking;
  memberDetails?: Member;
}

interface AlertMessageProps {
  slotServices: SlotServices;
  selectedNumberOfParticipants?: number;
  isServiceInCart?: boolean;
}

interface ErrorMessageProps {
  errors: FormError[];
}

const ButtonsContainer: React.FC<ButtonContainerProps> = ({
  serviceData,
  isCart,
  status,
  isServiceInCart,
  errors,
  selectedNumberOfParticipants,
  existingBookingDetails,
  memberDetails,
  isSlotUnavailable,
}) => {
  const { isUpsellPluginInstalled, isPricingPlansMigratedToEcom } =
    useFormComponentContext();
  const { experiments } = useExperiments();
  const { onBuyPlanAndBookWouldShow } = useFormActions();

  // A/B exposure: report whenever the current selection would surface Plan & Book, whether
  // or not the FT is on — see `wouldShowBuyPlanAndBook`. Keyed on the boolean rather than
  // reported during render, so it fires again only when the exposure state actually flips.
  const wouldShowExposure = wouldShowBuyPlanAndBook({
    slotServices: serviceData.slotServices,
    experiments,
    isPricingPlansMigratedToEcom,
  });

  useEffect(() => {
    if (wouldShowExposure) {
      onBuyPlanAndBookWouldShow();
    }
  }, [wouldShowExposure]);

  const slotServicesWithBuyPlans = getSelectedServicesForBuyPlan(
    serviceData.slotServices,
  );
  const exceedLimitedPricePlans = getExceedLimitedPricePlans(
    serviceData.slotServices,
    selectedNumberOfParticipants,
  );

  const hasNoAvailablePlans =
    experiments.enabled(
      ExperimentsConsts.BlockBuyPlanWhenNoAvailablePlansUoU,
    ) && hasServicesWithoutAvailablePlans(serviceData.slotServices);

  // Drop the cart ctas for a purchasable plan — it can't share the eCom cart, and
  // submitting always routes through the Plan & Book flow whichever cta was pressed,
  // so an "Add to cart" cta here would silently send the buyer to checkout. Unlike the
  // legacy `BUY_PLAN` branch above we keep the book cta rather than `BuyPlanButton`,
  // because Plan & Book must submit the booking, not redirect to Pricing Plans.
  const isPurchasablePlanSelected = hasPurchasablePlanSelected({
    slotServices: serviceData.slotServices,
    experiments,
    isPricingPlansMigratedToEcom,
  });

  let buttons;

  if (slotServicesWithBuyPlans.length || exceedLimitedPricePlans.length) {
    buttons = (
      <BuyPlanButton
        slotServices={serviceData.slotServices}
        status={status}
        disabled={isSlotUnavailable || hasNoAvailablePlans}
      />
    );
  } else if (isCart && !isPurchasablePlanSelected) {
    buttons = (
      <>
        <AddCartButton status={status} disabled={isSlotUnavailable} />
        {!isServiceInCart && !isUpsellPluginInstalled && (
          <BookButton
            slotServices={serviceData.slotServices}
            cartFlow={CartFlow.CHECKOUT}
            status={status}
            primary={false}
            disabled={isSlotUnavailable}
          />
        )}
      </>
    );
  } else {
    buttons = (
      <BookButton
        slotServices={serviceData.slotServices}
        status={status}
        disabled={isSlotUnavailable}
      />
    );
  }

  return (
    <div className={classes.root}>
      <RenderNotificationMessage
        slotServices={serviceData.slotServices}
        memberDetails={memberDetails}
        existingBookingDetails={existingBookingDetails}
      />
      <RenderAlertMessage
        slotServices={serviceData.slotServices}
        selectedNumberOfParticipants={selectedNumberOfParticipants}
        isServiceInCart={isServiceInCart}
      />
      <RenderErrorMessage errors={errors} />
      {hasNoAvailablePlans && <RenderNoAvailablePlansMessage />}
      <div className={classes.buttons}>{buttons}</div>
    </div>
  );
};

const RenderNoAvailablePlansMessage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <SectionMessage
      type={SectionMessageType.Alert}
      text={t('app.bookings-form.right-side-alert.no-available-plans')}
      dataHook={ButtonsContainerDataHooks.NO_AVAILABLE_PLANS_MESSAGE}
      withoutIcon
    />
  );
};

const RenderNotificationMessage: React.FC<NotificationMessageProps> = ({
  slotServices,
  memberDetails,
  existingBookingDetails,
}) => {
  const shouldShowAlreadyBookedAlert = existingBookingDetails && !memberDetails;

  const { t } = useTranslation();
  const { dateRegionalSettingsLocale } = useFormComponentContext();

  let alertText: string = '';

  if (shouldShowAlreadyBookedAlert) {
    const slotService = mapToArray<SlotService>(slotServices)[0];
    const startTime = formatBookingTime(
      slotService,
      existingBookingDetails,
      dateRegionalSettingsLocale,
    );
    const serviceName = slotService.service.name;
    alertText = t('app.bookings-form.booking-details.double-booking-warning', {
      startTime,
      serviceName,
    });
  }

  if (!alertText) {
    return null;
  }

  return (
    <SectionMessage
      type={SectionMessageType.Default}
      text={alertText}
      withoutIcon
    />
  );
};

const RenderAlertMessage: React.FC<AlertMessageProps> = ({
  slotServices,
  selectedNumberOfParticipants,
}) => {
  const { t } = useTranslation();
  const { onConflictAlertShown } = useFormActions();

  let alertText: string = '';

  const isDifferentBuyPlan = checkDifferentBuyPlans(slotServices);
  const exceedPlans = getExceedLimitedPricePlans(
    slotServices,
    selectedNumberOfParticipants,
  );

  if (exceedPlans?.length) {
    const planNames = exceedPlans.map((plan) => plan.label).join(', ');
    alertText = t(
      'app.bookings-form.right-side-alert.not-enough-sessions-left-price-plan',
      { planNames },
    );
  } else if (isDifferentBuyPlan) {
    alertText = t('app.bookings-form.right-side-alert.too-many-plans');
  }

  useEffect(() => {
    if (alertText) {
      onConflictAlertShown(alertText);
    }
  }, [alertText]);

  if (!alertText) {
    return null;
  }

  return (
    <SectionMessage
      type={SectionMessageType.Alert}
      text={alertText}
      withoutIcon
    />
  );
};

const RenderErrorMessage: React.FC<ErrorMessageProps> = ({ errors }) => {
  const { t } = useTranslation();

  const error = getErrorByType({
    errorType: CreateBookingErrorType,
    errors,
  });

  const getErrorMessage = useMemo(() => {
    if (error) {
      switch (error.errorType) {
        case CreateBookingErrorType.SESSION_CAPACITY_EXCEEDED:
        case CreateBookingErrorType.SCHEDULE_CAPACITY_EXCEEDED:
          // The multi-participant flow re-reads availability before submitting, so there it
          // can say how many spots are actually left and what to do about it. Everywhere else
          // the count is unknown and the message stays as it was.
          return error.params?.availableSpots !== undefined
            ? t('app.server-errors.not-enough-spots-left-for-participants', {
                availableSpots: error.params.availableSpots,
              })
            : t('app.server-errors.not-enough-spots-left');
        case CreateBookingErrorType.NON_SUFFICIENT_FUNDS:
          return t('app.server-errors.not-enough-funds');
        case CreateBookingErrorType.VIOLATIONS_WITH_ERROR_SEVERITY:
          return error.params?.message;
        case CreateBookingErrorType.BOOKING_POLICY_VIOLATION:
          return t('app.server-errors.booking-policy-violation');
        case CreateBookingErrorType.INVALID_CHOICES:
          return t('app.server-errors.invalid-choices');
        default:
          return t('app.server-errors.slot-not-available');
      }
    }
    return '';
  }, [error]);

  return (
    <SectionMessage type={SectionMessageType.Error} text={getErrorMessage} />
  );
};

export default ButtonsContainer;
